import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LOGO_URL, getUser, api } from "../api";
import { Bell, Search, Newspaper, MessageCircle, Folder, HeartPulse, CalendarCheck, CalendarDays, Home, Ticket, ShieldCheck, Stethoscope, Store, Package, UserPlus } from "lucide-react";
import { useEffect, useState, useCallback, useRef } from "react";
import { SearchPanel } from "./SearchPanel";
import { AccountMenu } from "./AccountMenu";
import { AddPatientModal } from "./AddPatientModal";
import { canClinical } from "../api";

// Matrice icone di navigazione per ruolo (una sola sorgente: desktop header + mobile tab bar)
function useNavItems(u, chatUnread) {
  const items = [{ key: "search", icon: Search, labelKey: "nav.search", testid: "nav-search" }];
  if (u?.role === "user") return [
    { key: "home", to: "/feed", icon: Home, labelKey: "nav.home", testid: "nav-home" },
    { key: "bio", to: "/bio", icon: HeartPulse, labelKey: "nav.bio", testid: "nav-bio" },
    { key: "drive", to: "/drive", icon: Folder, labelKey: "nav.drive", testid: "nav-drive" },
    { key: "bookings", to: "/bookings", icon: CalendarCheck, labelKey: "nav.bookings", testid: "nav-bookings" },
    { key: "orders", to: "/orders", icon: Package, labelKey: "shop.orders", testid: "nav-orders" },
    { key: "chat", to: "/chat", icon: MessageCircle, labelKey: "nav.chat", testid: "nav-chat", badge: chatUnread }];
  if (u?.role === "professional") return [
    { key: "home", to: `/professionals/${u.id}`, icon: Home, labelKey: "nav.home", testid: "nav-home" },
    { key: "agenda", to: "/agenda", icon: CalendarDays, labelKey: "nav.agenda", testid: "nav-agenda" },
    { key: "bookings", to: "/bookings", icon: CalendarCheck, labelKey: "nav.bookings", testid: "nav-bookings" },
    { key: "chat", to: "/chat", icon: MessageCircle, labelKey: "nav.chat", testid: "nav-chat", badge: chatUnread }];
  if (u?.role === "organization") return [
    { key: "home", to: `/organizations/${u.id}`, icon: Home, labelKey: "nav.home", testid: "nav-home" },
    { key: "myevents", to: "/my-events", icon: Ticket, labelKey: "events.my_events", testid: "nav-myevents" },
    { key: "services", to: "/services", icon: Stethoscope, labelKey: "services.nav", testid: "nav-services" },
    { key: "shop", to: "/shop", icon: Store, labelKey: "shop.nav", testid: "nav-shop" },
    { key: "chat", to: "/chat", icon: MessageCircle, labelKey: "nav.chat", testid: "nav-chat", badge: chatUnread }];
  if (u?.role === "admin") return [...items,
    { key: "feed", to: "/feed", icon: Newspaper, labelKey: "nav.feed", testid: "nav-feed" },
    { key: "events", to: "/events", icon: CalendarDays, labelKey: "nav.events", testid: "nav-events" },
    { key: "chat", to: "/chat", icon: MessageCircle, labelKey: "nav.chat", testid: "nav-chat", badge: chatUnread },
    { key: "admin", to: "/admin", icon: ShieldCheck, labelKey: "nav.admin", testid: "nav-admin" }];
  return items; // pubblico: solo Cerca
}

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const loc = useLocation();
  const u = getUser();
  const [chatUnread, setChatUnread] = useState(0);
  const [bellUnread, setBellUnread] = useState(0);
  const [showAddPatient, setShowAddPatient] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchDeskRef = useRef(null);
  const searchMobRef = useRef(null);
  const searchHdrRef = useRef(null);  // pulsante Cerca nell'header (solo ruolo utente)
  // Chiusura ricerca: il focus torna alla lente che l'ha aperta (variante visibile)
  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setTimeout(() => (searchHdrRef.current || (window.innerWidth >= 1024 ? searchDeskRef.current : searchMobRef.current))?.focus(), 0);
  }, []);
  const items = useNavItems(u, chatUnread);

  // Badge: polling (meccanismo chat esistente) + refresh immediato all'apertura conversazione
  const refreshBadges = useCallback(() => {
    if (!getUser()) { setChatUnread(0); setBellUnread(0); return; }
    api.get("/chat/unread-count").then(({data}) => setChatUnread(data.unread)).catch(() => {});
    api.get("/notifications/unread-count").then(({data}) => setBellUnread(data.unread)).catch(() => {});
  }, []);
  useEffect(() => {
    refreshBadges();
    const i = setInterval(refreshBadges, 10000);
    window.addEventListener("chat-updated", refreshBadges);
    return () => { clearInterval(i); window.removeEventListener("chat-updated", refreshBadges); };
  }, [refreshBadges, loc.pathname]);
  // Guardia: con cambio password obbligatorio si può stare solo su /settings
  useEffect(() => {
    if (u?.must_change_password && loc.pathname !== "/settings") nav("/settings?force=1");
  }, [loc.pathname]);
  // Guardia: profilo senza immagini obbligatorie -> percorso guidato
  useEffect(() => {
    if (!u || u.must_change_password || u.profile_complete !== false) return;
    const allowed = ["/settings", "/legal", "/login", "/register"];
    if (loc.pathname !== "/" && !allowed.some(p => loc.pathname.startsWith(p))) nav("/settings?complete=1");
  }, [loc.pathname]);

  // Bottom tab bar mobile sempre visibile e correttamente posizionata: nessun auto-hide su scroll
  // (evita l'effetto "barra che sparisce / icone mancanti" e i falsi artefatti di scroll su pagine corte).
  // Il footer non viene coperto grazie alla clearance inferiore (yp-footer-pad + safe-area).
  useEffect(() => { setSearchOpen(false); window.scrollTo(0, 0); }, [loc.pathname]);

  const langs = [["it","IT"],["en","EN"],["de","DE"],["fr","FR"]];
  const change = (l) => { i18n.changeLanguage(l); localStorage.setItem("lang", l); };
  const isActive = (to) => to && (loc.pathname === to || (to !== "/" && loc.pathname.startsWith(to + "/")));
  // Nella sala videoconsulto la navbar mobile globale viene sostituita dalla barra comandi contestuale (Batch D).
  const inVideoRoom = /^\/video\//.test(loc.pathname);

  const chatBadge = (testid) => chatUnread > 0 && (
    <span className="absolute top-1 right-1 yp-badge yp-badge-approved !px-1 !py-0 text-[10px] leading-4 min-w-[16px] text-center"
      data-testid={testid} role="status" aria-label={`${chatUnread}`}>{chatUnread}</span>);

  return (<>
    <header className="yp-nav" data-testid="main-navbar">
      <div className="yp-container relative flex items-center justify-between py-3 gap-2 lg:gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="brand-logo-link"
          onClick={() => window.scrollTo(0, 0)}>
          <img src={LOGO_URL} alt="YouPAM" className="h-6 md:h-[26px] w-auto object-contain" />
        </Link>
        {/* Desktop/tablet: icone nella zona centrale */}
        <nav className="hidden lg:flex flex-1 items-center justify-center gap-1" aria-label={t("nav.main")} data-testid="desktop-icon-nav">
          {items.map(it => it.key === "search" ? (
            <button key="search" ref={searchDeskRef} className={`yp-navicon ${searchOpen ? "yp-navicon-active" : ""}`} data-search-root
              onClick={() => setSearchOpen(o => !o)} aria-label={t(it.labelKey)} aria-expanded={searchOpen} data-testid={it.testid}>
              <Search size={22} strokeWidth={1.8} aria-hidden="true" />
              <span className="yp-navtip" role="tooltip">{t(it.labelKey)}</span>
            </button>
          ) : (
            <Link key={it.key} to={it.to} className={`yp-navicon ${isActive(it.to) ? "yp-navicon-active" : ""}`}
              aria-label={t(it.labelKey)} aria-current={isActive(it.to) ? "page" : undefined} data-testid={it.testid}>
              <it.icon size={22} strokeWidth={1.8} aria-hidden="true" />
              {it.key === "chat" && chatBadge("nav-chat-badge")}
              <span className="yp-navtip" role="tooltip">{t(it.labelKey)}</span>
              <span className="sr-only">{t(it.labelKey)}</span>
            </Link>))}
          {/* §A1: icona "Aggiungi paziente" immediatamente a destra della Chat (solo Pro / Struttura clinica) */}
          {canClinical(u) && (
            <button className="yp-navicon" onClick={() => setShowAddPatient(true)} aria-label={t("clinical.add_patient")} data-testid="nav-add-patient">
              <UserPlus size={22} strokeWidth={1.8} aria-hidden="true" />
              <span className="yp-navtip" role="tooltip">{t("clinical.add_patient")}</span>
            </button>)}
        </nav>
        <div className="flex items-center gap-1 lg:gap-2 shrink-0">
          {!u && (
            <select className="yp-input !py-1 !w-auto text-xs" data-testid="lang-select-button" aria-label="Language" value={i18n.language} onChange={(e) => change(e.target.value)}>
              {langs.map(([k,v]) => <option key={k} value={k}>{v}</option>)}
            </select>)}
          {u ? (<>
            {u.role === "user" && (
              <button ref={searchHdrRef} className="p-2 relative min-w-[44px] min-h-[44px] flex items-center justify-center" data-search-root
                onClick={() => setSearchOpen(o => !o)} aria-label={t("nav.search")} aria-expanded={searchOpen} data-testid="nav-search">
                <Search size={18} strokeWidth={1.8} aria-hidden="true" />
              </button>)}
            {(u.role === "professional" || u.role === "organization") && (
              <Link to={u.role === "organization" ? `/organizations/${u.id}/followers` : `/professionals/${u.id}/followers`}
                className={`p-2 relative min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg ${loc.pathname.endsWith("/followers") ? "text-blue-600 bg-blue-50" : "text-slate-600"}`}
                data-testid="nav-followers" aria-label={t("nav.followers")}>
                <Search size={18} strokeWidth={1.8} aria-hidden="true" />
              </Link>)}
            <Link to="/notifications" className={`p-2 relative min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg ${isActive("/notifications") ? "text-blue-600 bg-blue-50" : "text-slate-600"}`} data-testid="nav-notifications" aria-current={isActive("/notifications") ? "page" : undefined} aria-label={`${t("notif.title")}${bellUnread ? `, ${bellUnread}` : ""}`}>
              <Bell size={18} />
              {bellUnread > 0 && <span className="absolute top-0 right-0 yp-badge yp-badge-rejected !px-1 !py-0 text-[10px] leading-4" data-testid="nav-bell-badge" role="status">{bellUnread}</span>}
            </Link>
            <AccountMenu />
          </>) : (<>
            <Link to="/login" className="yp-btn yp-btn-outline !py-1 !px-2 lg:!px-3 text-xs" data-testid="nav-login">{t("nav.login")}</Link>
            <button onClick={() => window.dispatchEvent(new Event("open-register-choice"))} className="yp-btn yp-btn-primary !py-1 !px-2 lg:!px-3 text-xs" data-testid="nav-register">{t("nav.register")}</button>
          </>)}
        </div>
        <SearchPanel open={searchOpen} onClose={closeSearch} />
      </div>
    </header>
    {/* Mobile: bottom tab bar fissata in basso (sostituita dalla barra comandi nella sala video) */}
    {!inVideoRoom && (
    <nav className="yp-tabbar lg:hidden" aria-label={t("nav.main")} data-testid="bottom-tabbar">
      {items.map(it => it.key === "search" ? (
        <button key="search" ref={searchMobRef} className={`yp-tab ${searchOpen ? "yp-tab-active" : ""}`} data-search-root
          onClick={() => setSearchOpen(o => !o)} aria-label={t(it.labelKey)} aria-expanded={searchOpen} data-testid="nav-search-m">
          <Search size={22} strokeWidth={1.8} aria-hidden="true" />
        </button>
      ) : (
        <Link key={it.key} to={it.to} className={`yp-tab ${isActive(it.to) ? "yp-tab-active" : ""}`}
          aria-label={t(it.labelKey)} aria-current={isActive(it.to) ? "page" : undefined} data-testid={`${it.testid}-m`}>
          <span className="relative">
            <it.icon size={22} strokeWidth={1.8} aria-hidden="true" />
            {it.key === "chat" && chatBadge("nav-chat-badge-m")}
          </span>
          <span className="sr-only">{t(it.labelKey)}</span>
        </Link>))}
      {canClinical(u) && (
        <button className="yp-tab" onClick={() => setShowAddPatient(true)} aria-label={t("clinical.add_patient")} data-testid="nav-add-patient-m">
          <UserPlus size={22} strokeWidth={1.8} aria-hidden="true" />
        </button>)}
    </nav>)}
    {showAddPatient && <AddPatientModal onClose={() => setShowAddPatient(false)} />}
  </>);
}

export function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="mt-16 border-t border-slate-200 py-8 bg-slate-50 yp-footer-pad">
      <div className="yp-container">
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-slate-500" data-testid="footer-links">
          <Link to="/legal/privacy" data-testid="footer-privacy">{t("footer.privacy")}</Link>
          <Link to="/legal/terms" data-testid="footer-terms">{t("footer.terms")}</Link>
          <Link to="/legal/community" data-testid="footer-community">{t("footer.community")}</Link>
          <Link to="/legal/contacts" data-testid="footer-contacts">{t("footer.contacts")}</Link>
        </div>
      </div>
    </footer>
  );
}

export function Layout({ children }) {
  // La clearance inferiore per la bottom tab bar è sul footer (yp-footer-pad): il footer è sempre l'ultimo elemento
  return <div className="min-h-screen flex flex-col"><Navbar /><main className="flex-1">{children}</main><Footer /></div>;
}
